// usePanelHistory.ts
import { useState } from 'react';

import type { IRightSection, IRightSectionSnapshot } from '../components/reusable/right-panel/utils';

export interface IUsePanelHistory {
	current: IRightSectionSnapshot;
	goTo: (panel: IRightSection<{}>) => void;
	goBack: () => void;
	goForward: () => void;
	canGoBack: boolean;
	canGoForward: boolean;
	reset: () => void;
}

/**
 * Capture the restorable slice of the live panel state. History entries must be
 * value snapshots, not references to the live context object — the live context
 * keeps mutating as child panels open.
 */
const toSnapshot = (panel: IRightSection<{}>): IRightSectionSnapshot => ({
	action: panel.action,
	title: panel.title,
	data: panel.data,
	slug: panel.slug,
	cta: panel.cta,
	max: panel.max,
	compact: panel.compact,
	half: panel.half,
	quiet: panel.quiet,
	resultType: panel.resultType,
	breadcrumb: panel.breadcrumb,
});

export const usePanelHistory = (rightPanel: IRightSection<{}>): IUsePanelHistory => {
	const [stack, setStack] = useState<IRightSectionSnapshot[]>(rightPanel ? [toSnapshot(rightPanel)] : []);
	const [index, setIndex] = useState(0);

	const current = stack[index] || null;

	const goTo = (panel: IRightSection<{}>) => {
		const newStack = stack.slice(0, index + 1);
		newStack.push(toSnapshot(panel));
		setStack(newStack);
		setIndex(newStack.length - 1);
	};

	// Back/forward must restore the snapshot into the live context — panels read
	// their `data`/`title`/… from `useRightPanelContext()`, so moving the index
	// alone remounts the older component against the newest panel's data
	// (wrong scope/id — e.g. Agent Details falling back to the first agent).
	const goBack = () => {
		if (index > 0) {
			const target = stack[index - 1];
			setIndex((i) => i - 1);
			// Skip the sentinel stack[0] (pre-open empty action) — never force-restore it.
			if (target?.action?.component) rightPanel.restoreSection(target);
		}
	};

	const goForward = () => {
		if (index < stack.length - 1) {
			const target = stack[index + 1];
			setIndex((i) => i + 1);
			if (target?.action?.component) rightPanel.restoreSection(target);
		}
	};

	const reset = () => {
		setStack(rightPanel ? [toSnapshot(rightPanel)] : []);
		setIndex(0);
	};

	return {
		current,
		goTo,
		goBack,
		goForward,
		canGoBack: index > 1,
		canGoForward: index < stack.length - 1,
		reset,
	};
};
